import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowRight, ArrowLeft, Check, X, Building2, 
  DollarSign, FileCheck, AlertCircle, Rocket,
  Mail, Phone, User, Briefcase, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import SEO from '../components/shared/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier configurations
const TIERS = [
  {
    id: 'foundation',
    name: 'Foundation',
    price: 997,
    color: '#CD7F32',
    description: 'Business Foundation Package',
    features: ['LLC formation guidance', 'EIN registration', 'DOT/MC authority filing', 'BOC-3 coordination', 'UCR guidance']
  },
  {
    id: 'accelerator',
    name: 'Accelerator',
    price: 1997,
    color: '#C0C0C0',
    description: 'Authority + Compliance Setup',
    features: ['Everything in Foundation', 'Drug & Alcohol Consortium setup', 'Compliance calendar', '30 Days Monitoring', 'Carrier packet template']
  },
  {
    id: 'elite',
    name: 'Elite',
    price: 2997,
    color: '#D4AF37',
    description: 'Full Business Launch',
    popular: true,
    features: ['Everything in Accelerator', 'Business bank setup guidance', 'Factoring setup', '60 Days Monitoring', '30 Days Dispatch Support']
  }
];

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {[1, 2, 3, 4].slice(0, totalSteps).map((step) => (
      <div key={step} className="flex items-center">
        <div className={`w-10 h-10 flex items-center justify-center text-sm font-bold transition-all ${
          step < currentStep 
            ? 'bg-emerald-500 text-white' 
            : step === currentStep 
              ? 'bg-[#D4AF37] text-black' 
              : 'bg-zinc-800 text-zinc-500'
        }`}>
          {step < currentStep ? <Check className="w-5 h-5" /> : step}
        </div>
        {step < totalSteps && (
          <div className={`w-12 h-0.5 ${step < currentStep ? 'bg-emerald-500' : 'bg-zinc-800'}`} />
        )}
      </div>
    ))}
  </div>
);

// Question component
const Question = ({ question, value, onChange, required = true }) => (
  <div className="mb-6">
    <p className="text-white text-lg mb-4">{question}</p>
    <div className="flex gap-4">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`flex-1 py-4 px-6 border transition-all flex items-center justify-center gap-2 ${
          value === true 
            ? 'bg-emerald-500/10 border-emerald-500 text-emerald-400' 
            : 'bg-zinc-900 border-zinc-700 text-white hover:border-zinc-500'
        }`}
      >
        <Check className="w-5 h-5" />
        Yes
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`flex-1 py-4 px-6 border transition-all flex items-center justify-center gap-2 ${
          value === false 
            ? 'bg-red-500/10 border-red-500 text-red-400' 
            : 'bg-zinc-900 border-zinc-700 text-white hover:border-zinc-500'
        }`}
      >
        <X className="w-5 h-5" />
        No
      </button>
    </div>
  </div>
);

// Checkbox agreement component
const AgreementCheckbox = ({ text, checked, onChange }) => (
  <label className="flex items-start gap-4 mb-4 cursor-pointer group">
    <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center border transition-all mt-0.5 ${
      checked 
        ? 'bg-[#D4AF37] border-[#D4AF37]' 
        : 'bg-zinc-900 border-zinc-700 group-hover:border-zinc-500'
    }`}>
      {checked && <Check className="w-4 h-4 text-black" />}
    </div>
    <span className="text-white/80 text-sm leading-relaxed">{text}</span>
    <input 
      type="checkbox" 
      checked={checked} 
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only"
    />
  </label>
);

// Tier selection card
const TierCard = ({ tier, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(tier.id)}
    className={`w-full p-6 border text-left transition-all ${
      selected 
        ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
        : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
    }`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <span 
          className="px-3 py-1 text-sm font-bold"
          style={{ backgroundColor: `${tier.color}20`, color: tier.color }}
        >
          {tier.name}
        </span>
        {tier.popular && (
          <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold">
            POPULAR
          </span>
        )}
      </div>
      <div className={`w-6 h-6 border flex items-center justify-center ${
        selected ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-zinc-600'
      }`}>
        {selected && <Check className="w-4 h-4 text-black" />}
      </div>
    </div>
    <p className="text-zinc-400 text-sm mb-2">{tier.description}</p>
    <p className="text-3xl font-bold text-white">${tier.price.toLocaleString()}</p>
    <p className="text-zinc-500 text-xs mt-1">One-time fee</p>
  </button>
);

const StarterPackApplyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Contact info
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    // Step 1 - Business Status
    has_active_mc: null,
    llc_formed: null,
    ready_to_form_llc: null,
    // Step 2 - Financial Readiness
    understands_fees: null,
    has_startup_funds: null,
    can_secure_insurance: null,
    // Step 3 - Agreements
    understands_guidance: false,
    understands_no_filing: false,
    agrees_to_deadlines: false,
    // Tier selection
    selected_tier: searchParams.get('tier') || 'foundation'
  });

  // Check if step is complete
  const isStep1Complete = () => {
    if (formData.has_active_mc === true) return true; // Will redirect
    return formData.has_active_mc === false && 
           (formData.llc_formed === true || formData.ready_to_form_llc === true);
  };

  const isStep2Complete = () => {
    return formData.understands_fees === true && 
           formData.has_startup_funds === true && 
           formData.can_secure_insurance === true;
  };

  const isStep3Complete = () => {
    return formData.understands_guidance && 
           formData.understands_no_filing && 
           formData.agrees_to_deadlines;
  };

  const isContactComplete = () => {
    return formData.full_name.trim() && 
           formData.email.trim() && 
           formData.phone.trim() && 
           formData.company_name.trim();
  };

  // Check qualification
  const isQualified = () => {
    return formData.has_active_mc === false &&
           (formData.llc_formed === true || formData.ready_to_form_llc === true) &&
           formData.understands_fees === true &&
           formData.has_startup_funds === true &&
           formData.can_secure_insurance === true &&
           formData.understands_guidance &&
           formData.understands_no_filing &&
           formData.agrees_to_deadlines;
  };

  const handleSubmit = async () => {
    if (!isContactComplete() || !isQualified()) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await axios.post(`${API}/starter-pack/apply`, {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        company_name: formData.company_name,
        selected_tier: formData.selected_tier,
        qualification_answers: {
          has_active_mc: formData.has_active_mc,
          llc_formed: formData.llc_formed,
          ready_to_form_llc: formData.ready_to_form_llc,
          understands_fees: formData.understands_fees,
          has_startup_funds: formData.has_startup_funds,
          can_secure_insurance: formData.can_secure_insurance,
          understands_guidance: formData.understands_guidance,
          understands_no_filing: formData.understands_no_filing,
          agrees_to_deadlines: formData.agrees_to_deadlines
        }
      });
      
      if (response.data.qualified) {
        navigate('/build/success?status=approved');
      } else {
        navigate('/build/not-ready');
      }
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect if they have active MC
  useEffect(() => {
    if (formData.has_active_mc === true && step === 1) {
      navigate('/build/existing-authority');
    }
  }, [formData.has_active_mc, step, navigate]);

  const renderStep1 = () => (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-[#D4AF37]" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Business Status</h2>
          <p className="text-zinc-400 text-sm">Let's verify your current situation</p>
        </div>
      </div>
      
      <Question 
        question="Do you currently have an active MC authority?"
        value={formData.has_active_mc}
        onChange={(v) => setFormData({...formData, has_active_mc: v})}
      />
      
      {formData.has_active_mc === false && (
        <>
          <Question 
            question="Is your LLC already formed?"
            value={formData.llc_formed}
            onChange={(v) => setFormData({...formData, llc_formed: v})}
          />
          
          {formData.llc_formed === false && (
            <Question 
              question="Are you prepared to form your LLC within 7 days?"
              value={formData.ready_to_form_llc}
              onChange={(v) => setFormData({...formData, ready_to_form_llc: v})}
            />
          )}
        </>
      )}
      
      {formData.has_active_mc === false && formData.llc_formed === false && formData.ready_to_form_llc === false && (
        <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium">Not Ready Yet</p>
              <p className="text-zinc-400 text-sm mt-1">
                The Signature Program is designed for those ready to take action immediately. 
                Consider returning when you're prepared to form your LLC.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center">
          <DollarSign className="w-6 h-6 text-[#D4AF37]" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Financial Readiness</h2>
          <p className="text-zinc-400 text-sm">Confirm your financial preparation</p>
        </div>
      </div>
      
      <Question 
        question="Do you understand all government filing fees are your responsibility?"
        value={formData.understands_fees}
        onChange={(v) => setFormData({...formData, understands_fees: v})}
      />
      
      <Question 
        question="Do you have minimum startup funds available? (Filing fees, insurance deposits, etc.)"
        value={formData.has_startup_funds}
        onChange={(v) => setFormData({...formData, has_startup_funds: v})}
      />
      
      <Question 
        question="Are you prepared to secure insurance within 14 days of authority filing?"
        value={formData.can_secure_insurance}
        onChange={(v) => setFormData({...formData, can_secure_insurance: v})}
      />
      
      {(formData.understands_fees === false || formData.has_startup_funds === false || formData.can_secure_insurance === false) && (
        <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">Financial Preparation Required</p>
              <p className="text-zinc-400 text-sm mt-1">
                All questions must be answered "Yes" to proceed. The Signature Program requires 
                financial readiness to move quickly through the launch process.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center">
          <FileCheck className="w-6 h-6 text-[#D4AF37]" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Responsibility Agreement</h2>
          <p className="text-zinc-400 text-sm">Confirm your understanding and commitment</p>
        </div>
      </div>
      
      <div className="space-y-2">
        <AgreementCheckbox 
          text="I understand Queen B Logistics provides structured guidance and oversight. This is a professional service partnership, not a filing service."
          checked={formData.understands_guidance}
          onChange={(v) => setFormData({...formData, understands_guidance: v})}
        />
        
        <AgreementCheckbox 
          text="I understand Queen B Logistics does not file documents on my behalf. I am responsible for completing all government filings as directed."
          checked={formData.understands_no_filing}
          onChange={(v) => setFormData({...formData, understands_no_filing: v})}
        />
        
        <AgreementCheckbox 
          text="I agree to complete all assigned tasks within the specified deadlines. I understand delays may affect my launch timeline and overall success."
          checked={formData.agrees_to_deadlines}
          onChange={(v) => setFormData({...formData, agrees_to_deadlines: v})}
        />
      </div>
      
      {!isStep3Complete() && (formData.understands_guidance || formData.understands_no_filing || formData.agrees_to_deadlines) && (
        <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30">
          <p className="text-amber-400 text-sm">
            Please acknowledge all agreements to continue.
          </p>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center">
          <Rocket className="w-6 h-6 text-[#D4AF37]" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Complete Your Application</h2>
          <p className="text-zinc-400 text-sm">Select your package and provide contact details</p>
        </div>
      </div>
      
      {/* Tier Selection */}
      <div className="mb-8">
        <h3 className="text-white font-medium mb-4">Select Your Package</h3>
        <div className="grid gap-4">
          {TIERS.map((tier) => (
            <TierCard 
              key={tier.id}
              tier={tier}
              selected={formData.selected_tier === tier.id}
              onSelect={(id) => setFormData({...formData, selected_tier: id})}
            />
          ))}
        </div>
      </div>
      
      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-white font-medium">Contact Information</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-zinc-400 text-sm mb-2">Full Name *</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                placeholder="John Doe"
                className="w-full bg-zinc-900 border border-zinc-700 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] focus:outline-none"
                data-testid="full-name-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-zinc-400 text-sm mb-2">Company Name *</label>
            <div className="relative">
              <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                placeholder="ABC Trucking LLC"
                className="w-full bg-zinc-900 border border-zinc-700 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] focus:outline-none"
                data-testid="company-name-input"
              />
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-zinc-400 text-sm mb-2">Email Address *</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@example.com"
                className="w-full bg-zinc-900 border border-zinc-700 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] focus:outline-none"
                data-testid="email-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-zinc-400 text-sm mb-2">Phone Number *</label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(555) 123-4567"
                className="w-full bg-zinc-900 border border-zinc-700 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] focus:outline-none"
                data-testid="phone-input"
              />
            </div>
          </div>
        </div>
      </div>
      
      {submitError && (
        <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>
      )}
    </div>
  );

  const canProceed = () => {
    switch (step) {
      case 1: return isStep1Complete();
      case 2: return isStep2Complete();
      case 3: return isStep3Complete();
      case 4: return isContactComplete() && isQualified();
      default: return false;
    }
  };

  return (
    <div data-testid="starter-pack-apply-page" className="min-h-screen bg-[#0A0A0A]">
      <SEO 
        title="Apply for Signature Program - Queen B Logistics"
        description="Pre-qualification application for the Queen B Logistics Signature Program program. Build your trucking business the right way."
        canonical="https://queenblogistics.com/build/apply"
      />
      
      {/* Header */}
      <div className="bg-[#0D0D0D] border-b border-white/10 py-6">
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <Rocket className="w-6 h-6 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-medium">Signature Program Application</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white">
            Pre-Qualification
          </h1>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        <StepIndicator currentStep={step} totalSteps={4} />
        
        <div className="bg-[#121212] border border-zinc-800 p-6 sm:p-8">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          
          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-800">
            {step > 1 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
            ) : (
              <button
                onClick={() => navigate('/build')}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Cancel
              </button>
            )}
            
            {step < 4 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className={`flex items-center gap-2 px-6 py-3 font-bold transition-all ${
                  canProceed()
                    ? 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]'
                    : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || isSubmitting}
                className={`flex items-center gap-2 px-6 py-3 font-bold transition-all ${
                  canProceed() && !isSubmitting
                    ? 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]'
                    : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
                data-testid="submit-application-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Submit Application
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        
        {/* Progress Summary */}
        <div className="mt-6 p-4 bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-500">Step {step} of 4</span>
            <span className="text-zinc-400">
              {step === 1 && 'Business Status'}
              {step === 2 && 'Financial Readiness'}
              {step === 3 && 'Responsibility Agreement'}
              {step === 4 && 'Complete Application'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StarterPackApplyPage;
